import React from "react"
import { useController, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { CreateCredentialSchema } from "@/modules/admin/credentials"
import {
  CredentialNotesField,
  CredentialSecretFields,
  CredentialWebsiteField,
  ProviderSelectField,
} from "@/modules/admin/credentials"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { TextField } from "@/nzds/inputs/text-field/text-field"
import type { ResourceProvider } from "@/services/credential"

import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Chip from "@mui/material/Chip"
import Collapse from "@mui/material/Collapse"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

interface CreateCredentialFieldsProps {
  providers: ResourceProvider[]
  resourceOptions: string[]
  resourceProvidersIsLoading: boolean
  resourceTypesIsLoading: boolean
  submitErrorMessage: string | null
}

export const CreateCredentialFields: React.FC<CreateCredentialFieldsProps> = ({
  providers,
  resourceOptions,
  resourceProvidersIsLoading,
  resourceTypesIsLoading,
  submitErrorMessage,
}) => {
  const { t } = useTranslation("common")
  const form = useFormContext<CreateCredentialSchema>()
  const {
    field: {
      onBlur: onResourcesBlur,
      ref: resourcesRef,
      value: selectedResources,
    },
    fieldState: resourcesFieldState,
  } = useController({
    control: form.control,
    name: "resources",
  })

  const handleResourcesChange = (selectedValues: string[]) => {
    form.setValue("resources", selectedValues, {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  return (
    <Stack sx={{ gap: 2, pb: 1 }}>
      <Collapse in={Boolean(submitErrorMessage)} unmountOnExit>
        <Alert severity="error">{submitErrorMessage}</Alert>
      </Collapse>
      <Typography variant="body2">
        {t("admin.credentialsUi.purposeDescription")}
      </Typography>
      <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
        <ProviderSelectField
          disabled={resourceProvidersIsLoading}
          providers={providers}
        />
        <CredentialWebsiteField />
        <Autocomplete
          disableCloseOnSelect
          disabled={resourceTypesIsLoading}
          fullWidth
          loading={resourceTypesIsLoading}
          multiple
          noOptionsText={t("admin.credentialsUi.createDialog.noResources")}
          onChange={(_event, selectedValues) => {
            handleResourcesChange(selectedValues)
          }}
          options={resourceOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              error={Boolean(resourcesFieldState.error)}
              helperText={
                resourcesFieldState.error?.message ??
                t("admin.credentialsUi.resourcesHelperText")
              }
              inputRef={resourcesRef}
              label={t("admin.credentialsUi.resourcesLabel")}
              name="resources"
              onBlur={onResourcesBlur}
              required
              slotProps={{
                ...params.slotProps,
                // Disable native required; chips hold the value, not the input.
                // Preserve Autocomplete input slot props (chip startAdornment).
                input: {
                  ...params.slotProps?.input,
                  required: false,
                },
              }}
              variant="stacked"
            />
          )}
          renderValue={(resources, getItemProps) =>
            resources.map((resource, index) => {
              const { key: chipKey, ...itemProps } = getItemProps({ index })

              return (
                <li key={chipKey} style={{ listStyle: "none" }}>
                  <Chip
                    {...itemProps}
                    label={resource}
                    onDelete={itemProps.onDelete}
                    variant="outlined"
                  />
                </li>
              )
            })
          }
          value={selectedResources}
        />
        <CredentialSecretFields />
        <CredentialNotesField />
      </Stack>
    </Stack>
  )
}
